import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload } from "lucide-react";
import { DEFAULT_WPP_TEMPLATE } from "./empresaHelpers";
import { useCnpjCepLookup } from "./useCnpjCepLookup";

export function EmpresaLogoPicker({ f, set, logoRef, onUpload }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-28 h-28 border-2 border-dashed border-orange-200 rounded-xl flex items-center justify-center bg-orange-50/40 overflow-hidden shrink-0">
        {f.logo_base64
          ? <img src={f.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" data-testid="empresa-logo-preview"/>
          : <span className="text-[10px] text-muted-foreground text-center px-2">Sem logo</span>}
      </div>
      <div className="flex-1 space-y-2">
        <input ref={logoRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="empresa-logo-input"/>
        <Button variant="outline" onClick={() => logoRef.current?.click()} data-testid="btn-upload-logo">
          <Upload className="w-4 h-4 mr-2"/>Selecionar imagem
        </Button>
        <p className="text-xs text-muted-foreground">PNG, JPG ou SVG. Máx 2MB.</p>
        {f.logo_base64 && (
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => set({ logo_base64: "" })}>Remover</Button>
        )}
      </div>
    </div>
  );
}

// Dados cadastrais: nome, CNPJ (auto), IE, CEP (auto), endereço, contatos, segmento.
export function EmpresaDadosFields({ f, set }) {
  const lk = useCnpjCepLookup(f, set);
  return (
    <>
      <div className="md:col-span-2"><Label>Nome *</Label>
        <Input value={f.nome || ""} onChange={(e) => set({ nome: e.target.value })} data-testid="emp-nome" placeholder="Ex: Farmácia Bem Estar Ltda"/></div>
      <div><Label>CNPJ <span className="text-xs text-emerald-600">(auto-preenche)</span></Label>
        <div className="flex gap-1">
          <Input value={f.cnpj || ""} onChange={(e) => lk.onCnpjChange(e.target.value)} onBlur={lk.onCnpjBlur} data-testid="emp-cnpj" placeholder="00.000.000/0000-00" inputMode="numeric" maxLength={18}
            className={lk.cnpjInvalido ? "border-rose-400 focus-visible:ring-rose-400" : ""}/>
          <Button type="button" size="sm" variant="outline" onClick={lk.buscarCnpj} disabled={lk.cnpjLoading} data-testid="btn-buscar-cnpj" className="whitespace-nowrap">
            {lk.cnpjLoading ? "..." : "Buscar"}
          </Button>
        </div>
        {lk.cnpjInvalido && <p className="text-xs text-rose-600 mt-1" data-testid="cnpj-invalido">Dígito verificador inválido</p>}
      </div>
      <div><Label>Inscrição Estadual</Label>
        <Input value={f.inscricao_estadual || ""} onChange={(e) => set({ inscricao_estadual: e.target.value })} data-testid="emp-ie" placeholder="Isento ou número"/></div>
      <div>
        <Label>CEP <span className="text-xs text-emerald-600">(preenche endereço)</span></Label>
        <div className="flex gap-1">
          <Input value={f.cep || ""} onChange={(e) => lk.onCepChange(e.target.value)} onBlur={lk.onCepBlur} data-testid="emp-cep" placeholder="00000-000" inputMode="numeric" maxLength={9}/>
          <Button type="button" size="sm" variant="outline" onClick={lk.buscarCep} disabled={lk.cepLoading} data-testid="btn-buscar-cep" className="whitespace-nowrap">
            {lk.cepLoading ? "..." : "Buscar"}
          </Button>
        </div>
      </div>
      <div className="md:col-span-2"><Label>Endereço</Label>
        <Input value={f.endereco || ""} onChange={(e) => set({ endereco: e.target.value })} data-testid="emp-endereco" placeholder="Rua, número, bairro, cidade/UF"/></div>
      <div><Label>Telefone</Label>
        <Input value={f.telefone || ""} onChange={(e) => set({ telefone: e.target.value })} data-testid="emp-tel" placeholder="(00) 00000-0000"/></div>
      <div><Label>WhatsApp 1</Label>
        <Input value={f.whatsapp1 || ""} onChange={(e) => set({ whatsapp1: e.target.value })} data-testid="emp-whatsapp1" placeholder="(00) 90000-0000"/></div>
      <div><Label>WhatsApp 2</Label>
        <Input value={f.whatsapp2 || ""} onChange={(e) => set({ whatsapp2: e.target.value })} data-testid="emp-whatsapp2" placeholder="(00) 90000-0000"/></div>
      <div><Label>Email de Contato</Label>
        <Input type="email" value={f.email || ""} onChange={(e) => set({ email: e.target.value })} data-testid="emp-email" placeholder="contato@empresa.com"/></div>
      <div className="md:col-span-2"><Label>Segmento</Label>
        <Input value={f.segmento || ""} onChange={(e) => set({ segmento: e.target.value })} data-testid="emp-segmento" placeholder="Ex: Farmácia de manipulação e drogaria"/></div>
    </>
  );
}

// Multi-tenancy: slug do subdomínio, cor primária, tagline e template de WhatsApp.
export function EmpresaBrandingFields({ f, set }) {
  return (
    <>
      <div className="md:col-span-2 mt-2 pt-3 border-t">
        <div className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">Identidade do Tenant (subdomínio)</div>
      </div>
      <div><Label>Slug do subdomínio <span className="text-xs text-emerald-600">(auto)</span></Label>
        <Input value={f.slug || ""} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
          data-testid="emp-slug" placeholder="fazbem" className="mono lowercase"/>
        <p className="text-[11px] text-muted-foreground mt-1">
          Usado como <code>{"{slug}"}.usegestao.app.br</code>. Deixe vazio para gerar a partir do nome.
        </p>
      </div>
      <div><Label>Cor primária</Label>
        <div className="flex gap-2 items-center">
          <input type="color" value={f.cor_primaria || "#EA580C"} onChange={(e) => set({ cor_primaria: e.target.value })}
            data-testid="emp-cor-primaria" className="h-10 w-14 rounded border cursor-pointer"/>
          <Input value={f.cor_primaria || ""} onChange={(e) => set({ cor_primaria: e.target.value })} placeholder="#EA580C" className="mono flex-1"/>
        </div>
      </div>
      <div className="md:col-span-2"><Label>Tagline (frase da tela de login)</Label>
        <Input value={f.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} data-testid="emp-tagline"
          placeholder="Ex: A gestão de entregas da sua farmácia, do balcão à porta do cliente."/>
      </div>
      <div className="md:col-span-2">
        <Label>Mensagem WhatsApp (quando entrega ficar "Em trânsito")</Label>
        <Textarea rows={3} value={f.whatsapp_msg_template || ""} onChange={(e) => set({ whatsapp_msg_template: e.target.value })} data-testid="emp-wpp-template" placeholder={DEFAULT_WPP_TEMPLATE}/>
        <p className="text-xs text-muted-foreground mt-1">
          Variáveis disponíveis: <code className="bg-orange-50 px-1 rounded">{"{cliente}"}</code>, <code className="bg-orange-50 px-1 rounded">{"{pedido}"}</code>, <code className="bg-orange-50 px-1 rounded">{"{entregador}"}</code>, <code className="bg-orange-50 px-1 rounded">{"{hora}"}</code>, <code className="bg-orange-50 px-1 rounded">{"{empresa}"}</code>
          {" · "}Deixe em branco para usar a padrão.
        </p>
      </div>
    </>
  );
}
